import { type MutableRefObject } from 'react';

import {
  Button,
  Divider,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
  type TypeSelectOption,
} from '@thefarmersfront/kitchen';

import { FormField, ModalSection, NumberUnitInput } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useClaimStore } from '~/store/claim';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';
import { CLAIM_EXCHANGE_TYPE, CLAIM_TOTAL_BOX_OPTIONS, type TypeClaimExchangeType } from '~/constants/claim';

import type {
  TypeExchangeRegisterSecondStepErrorFieldRefs,
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

import styles from '../ClaimExchangeRegisterSecondStepContent.module.scss';

const ClaimExchangeRegisterSecondStepReturnInfo = ({
  data,
  dataState,
  onChange,
  onChangeState,
  errorFieldRefs,
}: {
  data: TypeExchangeRegisterSecondStepValues['returnInfo'];
  dataState: TypeExchangeRegisterSecondStepValuesState['returnInfo'];
  onChange: (values: Partial<TypeExchangeRegisterSecondStepValues>) => void;
  onChangeState: (values: Partial<TypeExchangeRegisterSecondStepValuesState>) => void;
  errorFieldRefs: MutableRefObject<TypeExchangeRegisterSecondStepErrorFieldRefs>;
}) => {
  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (_, address) => onChange({ returnInfo: { ...data, primaryAddress: address } }),
  });

  const selectOptions = useClaimStore((state) => state.selectOptions);

  const options = {
    returnPlace: selectOptions.returnPlace,
    accessMethod: selectOptions.accessMethod,
  };

  return (
    <ModalSection className={styles['return-info']} title="회수 정보">
      <FormField
        label="출고요청번호"
        required
        contentAlign="center"
        contents={<Text size="large">{data.outboundCode}</Text>}
      />
      <FormField
        label="원출고센터"
        required
        contentAlign="center"
        contents={<Text size="large">{data.outboundClusterCenter}</Text>}
      />
      <FormField
        label="원배송사"
        required
        contentAlign="center"
        contents={<Text size="large">{data.outboundCourier}</Text>}
      />
      <FormField
        label="회수배송사"
        required
        contentAlign="center"
        contents={<Text size="large">{data.returnCourier}</Text>}
      />
      <FormField
        label="배송사 전달사항"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={30}
            value={data.courierMemo}
            onChange={(e) => onChange({ returnInfo: { ...data, courierMemo: e.target.value } })}
            onBlur={(e) => onChange({ returnInfo: { ...data, courierMemo: e.target.value.trim() } })}
          />
        }
      />
      <FormField
        label="박스 총 수량"
        required
        tooltip={{
          maxWidth: '263px',
          description: '고객이 회수 요청한 박스의 총 수량입니다.\n부정확할 경우 택배사가 회수를 실패할 수 있습니다.',
        }}
        contents={
          <>
            <Select
              ref={(el) => {
                errorFieldRefs.current['returnInfo.totalBox'] = el;
              }}
              placeholder="선택"
              size="small"
              maxWidth="148px"
              options={CLAIM_TOTAL_BOX_OPTIONS}
              status={dataState.totalBox.status}
              validationMessage={dataState.totalBox.message}
              value={data.totalBox}
              onChange={(_: unknown, value: TypeSelectOption) => {
                if (value?.value === data.totalBox?.value) return;
                onChange({ returnInfo: { ...data, totalBox: value, totalBoxInput: '' } });
              }}
              onClick={() =>
                onChangeState({
                  returnInfo: {
                    ...dataState,
                    totalBox: DEFAULT_INPUT_STATUS,
                    totalBoxInput: DEFAULT_INPUT_STATUS,
                  },
                })
              }
            />
            {data.totalBox?.value === 'ETC' && (
              <NumberUnitInput
                ref={(el) => {
                  errorFieldRefs.current['returnInfo.totalBoxInput'] = el;
                }}
                placeholder="숫자만 입력"
                size="small"
                maxWidth="133px"
                unit="개"
                status={dataState.totalBoxInput.status}
                validationMessage={dataState.totalBoxInput.message}
                value={data.totalBoxInput}
                onChange={(e) => {
                  onChange({ returnInfo: { ...data, totalBoxInput: e.target.value } });
                  onChangeState({ returnInfo: { ...dataState, totalBoxInput: DEFAULT_INPUT_STATUS } });
                }}
                onBlur={(e) => onChange({ returnInfo: { ...data, totalBoxInput: e.target.value.trim() } })}
                onClick={() => onChangeState({ returnInfo: { ...dataState, totalBoxInput: DEFAULT_INPUT_STATUS } })}
              />
            )}
          </>
        }
        contentsClassName={styles['box-quantity-container']}
      />
      <FormField
        label="선교환여부"
        required
        contentAlign="center"
        tooltip={{
          maxWidth: '209px',
          description: '선교환 선택 시 반품 처리 이전에\n교환 배송 상품 출고가 우선 진행됩니다.',
        }}
        contents={
          <RadioGroup
            name="exchangeType"
            size="medium"
            value={data.exchangeType}
            onChange={(e) =>
              onChange({ returnInfo: { ...data, exchangeType: e.target.value as TypeClaimExchangeType } })
            }
          >
            <Radio label="일반교환" value={CLAIM_EXCHANGE_TYPE.NORMAL.value} />
            <Radio label="선교환" value={CLAIM_EXCHANGE_TYPE.PRE_EXCHANGE.value} />
          </RadioGroup>
        }
      />
      <Divider className={styles['divider']} color="gray100" />
      <FormField
        label="원주문 수령자명"
        required
        contentAlign="center"
        contents={<Text size="large">{data.receiverName}</Text>}
      />
      <FormField
        label="연락처"
        required
        contents={
          <Input
            ref={(el) => {
              errorFieldRefs.current['returnInfo.receiverContactNumber'] = el;
            }}
            type="text"
            placeholder="- 포함 입력"
            size="small"
            maxWidth="148px"
            status={dataState.receiverContactNumber.status}
            validationMessage={dataState.receiverContactNumber.message}
            value={data.receiverContactNumber}
            onChange={(e) => {
              const newValue = e.target.value;
              if (validateNumberAndHyphenOnly(newValue)) {
                onChange({ returnInfo: { ...data, receiverContactNumber: newValue } });
                onChangeState({ returnInfo: { ...dataState, receiverContactNumber: DEFAULT_INPUT_STATUS } });
              }
            }}
            onBlur={(e) => onChange({ returnInfo: { ...data, receiverContactNumber: e.target.value.trim() } })}
            onClick={() => onChangeState({ returnInfo: { ...dataState, receiverContactNumber: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="기본주소"
        required
        contents={
          <>
            <Input
              ref={(el) => {
                errorFieldRefs.current['returnInfo.primaryAddress'] = el;
              }}
              type="text"
              size="small"
              disabled
              status={dataState.primaryAddress.status}
              validationMessage={dataState.primaryAddress.message}
              value={data.primaryAddress}
            />
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                handleZipCodeSearchOpen();
                onChangeState({ returnInfo: { ...dataState, primaryAddress: DEFAULT_INPUT_STATUS } });
              }}
            >
              검색
            </Button>
          </>
        }
        contentsClassName={styles['address-container']}
      />
      <FormField
        label="상세주소"
        contents={
          <Input
            type="text"
            placeholder="입력"
            size="small"
            maxLength={30}
            count
            value={data.detailAddress}
            onChange={(e) => onChange({ returnInfo: { ...data, detailAddress: e.target.value } })}
            onBlur={(e) => onChange({ returnInfo: { ...data, detailAddress: e.target.value.trim() } })}
          />
        }
      />
      <FormField
        label="회수장소"
        required
        contents={
          <Select
            ref={(el) => {
              errorFieldRefs.current['returnInfo.returnPlace'] = el;
            }}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.returnPlace}
            status={dataState.returnPlace.status}
            validationMessage={dataState.returnPlace.message}
            value={data.returnPlace}
            onChange={(_: unknown, value: TypeSelectOption<string>) =>
              onChange({ returnInfo: { ...data, returnPlace: value } })
            }
            onClick={() => onChangeState({ returnInfo: { ...dataState, returnPlace: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="회수장소 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={30}
            value={data.returnPlaceDetail}
            onChange={(e) => onChange({ returnInfo: { ...data, returnPlaceDetail: e.target.value } })}
            onBlur={(e) => onChange({ returnInfo: { ...data, returnPlaceDetail: e.target.value.trim() } })}
          />
        }
      />
      <FormField
        label="출입방법"
        required
        contents={
          <Select
            ref={(el) => {
              errorFieldRefs.current['returnInfo.accessMethod'] = el;
            }}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.accessMethod}
            status={dataState.accessMethod.status}
            validationMessage={dataState.accessMethod.message}
            value={data.accessMethod}
            onChange={(_: unknown, value: TypeSelectOption<string>) =>
              onChange({ returnInfo: { ...data, accessMethod: value } })
            }
            onClick={() => onChangeState({ returnInfo: { ...dataState, accessMethod: DEFAULT_INPUT_STATUS } })}
          />
        }
      />
      <FormField
        label="출입방법 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={30}
            value={data.accessDetail}
            onChange={(e) => onChange({ returnInfo: { ...data, accessDetail: e.target.value } })}
            onBlur={(e) => onChange({ returnInfo: { ...data, accessDetail: e.target.value.trim() } })}
          />
        }
      />
    </ModalSection>
  );
};

export default ClaimExchangeRegisterSecondStepReturnInfo;
